import React, { useState } from 'react';
import {
  Modal,
  Button,
  Typography,
  Badge,
  Loading,
  useToast,
  Alert,
  AlertDescription,
} from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../../types/organization.types';
import { PlusCircle, Edit, Trash, List, AlertTriangle, Info } from 'lucide-react';
import { MLSFormModal } from './MLSFormModal';
import { ApprovedMLSListModal } from './ApprovedMLSListModal';
import { ConfirmationDialog } from '../../../../components/common/dialogs/ConfirmationDialog';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { useDeleteMLS } from '../../../../hooks/client/mls/useDeleteMLS';

interface MLSDetails {
  boardCode: string;
  agentId?: string[];
  officeId?: string[];
  enabled?: boolean;
}

interface MLSManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: Organization | undefined;
  onSuccess?: () => void;
  onCreateWebsite?: () => void; // New prop to handle create website action
  isSuperAdmin: boolean; // Controls whether user can subscribe/edit/delete MLS
}

export const MLSManagementModal: React.FC<MLSManagementModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
  onCreateWebsite,
  isSuperAdmin,
}) => {
  // Get tenant data to access subscribedMLSDetails
  const { data: tenant, isLoading: isTenantLoading } = useTenant(selectedOrg?.id, isOpen);
  const [searchInput] = useState('');
  const searchQuery = useDebounce(searchInput, 300);
  const [showMLSFormModal, setShowMLSFormModal] = useState(false);
  const [mlsFormMode, setMlsFormMode] = useState<'create' | 'update'>('create');
  const [selectedMLS, setSelectedMLS] = useState<MLSDetails | null>(null);
  const [mlsToDelete, setMlsToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApprovedMLSListModal, setShowApprovedMLSListModal] = useState(false);
  const { showToast } = useToast();

  // Use the delete MLS hook
  const { mutate: deleteMLS, isPending: isDeleting } = useDeleteMLS();

  // Filter MLS by search query
  const filteredMLS = React.useMemo(() => {
    if (!tenant?.subscribedMLSDetails) return [];

    if (!searchQuery) return tenant.subscribedMLSDetails;

    return tenant.subscribedMLSDetails.filter((mls) =>
      mls.boardCode.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [tenant, searchQuery]);

  const handleUpdateMLS = (boardCode: string) => {
    const mls = filteredMLS.find((m) => m.boardCode === boardCode);
    if (mls) {
      setSelectedMLS(mls);
      setMlsFormMode('update');
      setShowMLSFormModal(true);
    }
  };

  const handleCreateMLS = () => {
    setSelectedMLS(null);
    setMlsFormMode('create');
    setShowMLSFormModal(true);
  };

  // Handle initiating delete process
  const handleDeleteClick = (boardCode: string) => {
    setMlsToDelete(boardCode);
    setShowDeleteConfirm(true);
  };

  // Handle confirming delete
  const handleConfirmDelete = () => {
    if (!mlsToDelete || !selectedOrg?.id) return;

    deleteMLS(
      { organizationId: selectedOrg.id, boardCode: mlsToDelete },
      {
        onSuccess: () => {
          showToast({
            message: `MLS ${mlsToDelete} has been unsubscribed successfully`,
            variant: 'success',
          });
          setShowDeleteConfirm(false);
          setMlsToDelete(null);
          // Trigger success callback if provided
          if (onSuccess) onSuccess();
        },
        onError: (error) => {
          showToast({
            message: error instanceof Error ? error.message : 'Failed to unsubscribe from MLS',
            variant: 'error',
          });
          setShowDeleteConfirm(false);
          setMlsToDelete(null);
        },
      }
    );
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setMlsToDelete(null);
  };

  // Render different content based on whether tenant exists
  const renderContent = () => {
    if (isTenantLoading) {
      return (
        <div className="flex justify-center items-center h-[300px]">
          <Loading />
        </div>
      );
    }

    if (!tenant) {
      return (
        <div className="flex flex-col justify-center items-center h-[300px] space-y-6">
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You need to create a website before you can manage MLS subscriptions.
            </AlertDescription>
          </Alert>
          <Button
            variant="primary"
            onClick={() => {
              onClose(); // Close this modal
              // Call the onCreateWebsite prop if provided
              if (onCreateWebsite) {
                onCreateWebsite();
              }
            }}
          >
            Create Website
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className="flex justify-between items-center mb-4">
          <div className="flex-1 mr-4">
            <Alert variant="info">
              <Info className="h-4 w-4" />
              <AlertDescription>
                {isSuperAdmin
                  ? 'Listed are the MLS subscribed to this organization. You can add subscribe more MLS or update them.'
                  : 'Listed are the MLS subscribed to this organization.'}
              </AlertDescription>
            </Alert>
          </div>
          {isSuperAdmin && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="md"
                onClick={() => setShowApprovedMLSListModal(true)}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <List className="h-4 w-4" />
                View All MLS
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleCreateMLS}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <PlusCircle className="h-4 w-4" />
                Subscribe MLS
              </Button>
            </div>
          )}
        </div>
        <div className="h-[500px] overflow-y-auto">
          {filteredMLS.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-gray-500 dark:text-gray-400">No MLS subscriptions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMLS.map((mls) => (
                <div
                  key={mls.boardCode}
                  className="border border-gray-300 dark:border-white/15 rounded-lg p-4 bg-white dark:bg-white/5"
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">{mls.boardCode}</h3>
                      </div>

                      <div className="flex gap-10">
                        {mls.agentId && mls.agentId.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              Agent IDs:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {mls.agentId.map((id) => (
                                <Badge key={id} variant="secondary" className="bg-foreground/10 text-foreground">
                                  {id}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {mls.officeId && mls.officeId.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              Office IDs:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {mls.officeId.map((id) => (
                                <Badge key={id} variant="secondary" className="bg-foreground/10 text-foreground">
                                  {id}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {isSuperAdmin && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateMLS(mls.boardCode)}
                          className="flex items-center gap-1"
                        >
                          <Edit className="w-4 h-4" />
                          Update
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleDeleteClick(mls.boardCode)}
                          className="flex items-center gap-1 !bg-red-600 hover:bg-red-400"
                        >
                          <Trash className="w-4 h-4" />
                          Unsubscribe
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <Modal.Header>
          <Typography variant="h3">MLS Management</Typography>
        </Modal.Header>

        <Modal.Body className="py-8">
          <div className="space-y-6">{renderContent()}</div>
        </Modal.Body>
      </Modal>

      <MLSFormModal
        isOpen={showMLSFormModal}
        onClose={() => setShowMLSFormModal(false)}
        selectedOrgId={selectedOrg?.id || ''}
        mode={mlsFormMode}
        boardCode={selectedMLS?.boardCode}
        existingAgentIds={selectedMLS?.agentId || []}
        existingOfficeIds={selectedMLS?.officeId || []}
        onSuccess={() => {
          // Trigger success callback if provided
          if (onSuccess) onSuccess();
        }}
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Unsubscribe from MLS"
        message={`Are you sure you want to unsubscribe from ${mlsToDelete}? This action cannot be undone.`}
        confirmLabel="Unsubscribe"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      <ApprovedMLSListModal
        isOpen={showApprovedMLSListModal}
        onClose={() => setShowApprovedMLSListModal(false)}
      />
    </>
  );
};
